import produce from 'immer';
import get from 'lodash.get';
import { create } from 'services/api';
import { MARKER_TYPES } from '../shared/helpers/markers';

export const ADD_EPISODE_MARKER = 'create/markers/ADD_EPISODE_MARKER';
export const DELETE_EPISODE_MARKER = 'create/markers/DELETE_EPISODE_MARKER';
export const RECEIVE_EPISODE_MARKER = 'create/markers/RECEIVE_EPISODE_MARKER';
export const RECEIVE_EPISODE_MARKERS = 'create/markers/RECEIVE_EPISODE_MARKERS';
export const PATCH_EPISODE_MARKER = 'create/markers/PATCH_EPISODE_MARKER';

const initialState = {};
const updateQueue = {};

export const selectMarkers = (state, episodeId) => get(state, ['v2', 'markers', episodeId], null);

export const selectAdMarkers = (state, episodeId) =>
  get(state, ['v2', 'markers', episodeId], []).filter(marker => marker.type === MARKER_TYPES.AD);

export const selectAudioMarker = (state, episodeId) =>
  get(state, ['v2', 'markers', episodeId], []).find(marker => marker.type === MARKER_TYPES.AUDIO);

// Legacy DB support: old episodes might have body=null when there was no ad placed.
const preventAdMarkerNullBody = markers =>
  markers.map(marker => {
    if (marker.type === MARKER_TYPES.AD && !marker.body) {
      // eslint-disable-next-line no-param-reassign
      marker.body = {};
    }

    return marker;
  });

/* eslint-disable no-param-reassign, default-case, consistent-return */
export default produce((state, { payload, type }) => {
  let indexToUpdate;

  switch (type) {
    case ADD_EPISODE_MARKER:
      if (state[payload.episode]) {
        state[payload.episode].push(payload);
      } else {
        state[payload.episode] = [payload];
      }

      break;

    case DELETE_EPISODE_MARKER:
      state[payload.episode] = state[payload.episode].filter(m => m.id !== payload.id);
      break;

    case PATCH_EPISODE_MARKER:
      indexToUpdate = state[payload.episodeId].findIndex(m => m.id === payload.markerId);
      state[payload.episodeId][indexToUpdate] = {
        ...state[payload.episodeId][indexToUpdate],
        ...payload.data,
      };
      break;

    case RECEIVE_EPISODE_MARKER:
      indexToUpdate = state[payload.episode].findIndex(m => m.id === payload.id);
      state[payload.episode][indexToUpdate] = payload;
      break;

    case RECEIVE_EPISODE_MARKERS:
      state[payload.id] = preventAdMarkerNullBody(payload.items);
      break;
  }
}, initialState);
/* eslint-enable no-param-reassign, default-case, consistent-return */

const markerHasPlacement = marker => marker.body && marker.body.placement;

const ensureAdMarkerPlacements = (markers = []) => {
  return produce(markers, draft => {
    const adMarkers = markers
      .filter(marker => marker.type === MARKER_TYPES.AD)
      .sort((a, b) => a.start - b.start);

    if (adMarkers.length > 0 && !markerHasPlacement(adMarkers[0])) {
      const preRollMarker = draft.find(m => m.id === adMarkers[0].id);
      preRollMarker.body = { ...preRollMarker.body, placement: 'preroll' };
    }

    if (adMarkers.length > 1 && !markerHasPlacement(adMarkers[adMarkers.length - 1])) {
      const postRollMarker = draft.find(m => m.id === adMarkers[adMarkers.length - 1].id);
      postRollMarker.body = { ...postRollMarker.body, placement: 'postroll' };
    }

    if (adMarkers.length > 2 && !markerHasPlacement(adMarkers[1])) {
      const midRollMarker = draft.find(m => m.id === adMarkers[1].id);
      midRollMarker.body = { ...midRollMarker.body, placement: 'midroll' };
    }
  });
};

export const fetchEpisodeMarkers = episodeId => dispatch => {
  return create.get(`/api-v3/episodes/${episodeId}/markers`).then(response => {
    const markers = ensureAdMarkerPlacements(response.body.items);

    dispatch({
      type: RECEIVE_EPISODE_MARKERS,
      payload: {
        ...response.body,
        items: markers,
      },
    });

    return markers;
  });
};

export const createMarker = ({
  body = {},
  episodeId,
  episodeDuration,
  markerDuration = 30,
  placement,
  start = 0,
  type = MARKER_TYPES.IMAGE,
}) => dispatch => {
  const marker = {
    body,
    duration: markerDuration,
    type,
  };

  if (type === MARKER_TYPES.AD) {
    marker.start = {
      preroll: 0,
      midroll: episodeDuration / 2,
      postroll: episodeDuration,
    }[placement];
    marker.body.placement = placement;
  } else {
    marker.start = start;
  }

  return create.post(`/api-v2/acasts/${episodeId}/blings`, marker).then(response => {
    dispatch({
      type: ADD_EPISODE_MARKER,
      payload: response.body,
    });

    return response.body;
  });
};

export const updateMarker = (episodeId, markerId, data) => dispatch => {
  const { timer, data: queuedData } = updateQueue[markerId] || {};

  if (timer) {
    clearTimeout(timer);
  }

  updateQueue[markerId] = { data: { ...queuedData, ...data } };

  updateQueue[markerId].timer = setTimeout(() => {
    create.patch(`/api-v2/blings/${markerId}`, updateQueue[markerId].data).then(response => {
      delete updateQueue[markerId];

      dispatch({
        type: RECEIVE_EPISODE_MARKER,
        payload: response.body,
      });
    });
  }, 1000);

  dispatch({
    type: PATCH_EPISODE_MARKER,
    payload: { episodeId, markerId, data },
  });
};

export const deleteMarker = marker => dispatch =>
  create.delete(`/api-v2/blings/${marker.id}`).then(() =>
    dispatch({
      type: DELETE_EPISODE_MARKER,
      payload: marker,
    })
  );

export const deleteMarkers = markers => dispatch => {
  return Promise.all(markers.map(marker => deleteMarker(marker)(dispatch)));
};

export const updateAudioMarkerWithDuration = (markerId, duration) => dispatch => {
  return create.patch(`/api-v2/blings/${markerId}`, { duration }).then(response => {
    dispatch({
      type: RECEIVE_EPISODE_MARKER,
      payload: response.body,
    });
  });
};

export const updateAudioMarkerWithMedia = (markerId, media) => dispatch => {
  return create.patch(`/api-v2/blings/${markerId}`, { body: { media: [media] } }).then(response => {
    dispatch({
      type: RECEIVE_EPISODE_MARKER,
      payload: response.body,
    });
  });
};

export const ensureAdMarkersExists = (episodeId, duration) => (dispatch, getState) => {
  const state = getState();
  // TODO: When the episode state has been moved from schema resource we should fetch this data from the v2 state
  const episode = state.episode.get(episodeId);
  const show = state.show.get(episode.show);
  const midrollThreshold = show.midrollThreshold || 1200;
  const adMarkers = selectAdMarkers(state, episodeId);
  const uniqueAdMarkers = [];
  const duplicateAdMarkers = [];
  const acceptedPlacements = ['preroll', 'postroll'];

  if (duration >= midrollThreshold) {
    acceptedPlacements.push('midroll');
  }

  const requests = [];

  adMarkers.forEach(marker => {
    const isDuplicatePlacement = uniqueAdMarkers.find(
      m => m.body.placement === marker.body.placement
    );
    const hasInvalidPlacement = !acceptedPlacements.find(p => p === marker.body.placement);

    if (isDuplicatePlacement || hasInvalidPlacement) {
      const notAlreadyAddedToDuplicates = !duplicateAdMarkers.find(m => m.id === marker.id);

      if (notAlreadyAddedToDuplicates) {
        duplicateAdMarkers.push(marker);
      }
    } else {
      uniqueAdMarkers.push(marker);
    }
  });

  duplicateAdMarkers.forEach(marker => {
    requests.push(deleteMarker(marker)(dispatch));
  });

  const missingPlacements = acceptedPlacements.filter(
    placement => !uniqueAdMarkers.find(marker => marker.body.placement === placement)
  );

  missingPlacements.forEach(placement => {
    requests.push(
      createMarker({ episodeId, episodeDuration: duration, placement, type: MARKER_TYPES.AD })(
        dispatch
      )
    );
  });

  return Promise.all(requests);
};
